import get from 'lodash.get';

import isObject from './isObject.js';
import json from './json.js';
import appError from './appError.js';
import logRequest from './logRequest.js';
import sleep from './sleep.js';
import serializeUrl from './serializeURL.js';

/**
 * Array of timeouts before the request is marked as failed (in MS)
 */
const requestRetryTimeout = [1500, 3000, 5000];

/**
 * Regex of status codes which would trigger request retry attempt
 */
const requestRetryPattern = /^(-1|0|408|5[0-9]{2})$/;

/**
 * On client, check if response is missing all headers or our custom application headers
 * @param {string[][]} headersEntries Entries array of response headers
 * @param {object} session Current session information the call was made in
 */
const checkMissingHeaders = (headersEntries = [], session) => {
	if (IS_CLIENT && !headersEntries.length) {
		appError('Response is missing headers', {
			code: 'APP_SHARED_UTIL',
			origin: 'makeRequest',
			status: 500,
			detail: {
				headers: headersEntries,
				session
			}
		});
	}
};

/**
 * Make fetch request
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Request
 * @see https://github.com/bitinn/node-fetch
 * @param {string} path Request path
 * @param {object} [options] Request options
 * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} [options.method] Request method
 * @param {object} [options.form] If set, request will be made with application/x-www-form-urlencoded header and correct body
 * @param {object} [options.json] If set, request will be made with application/json header and correct body
 * @param {object} [options.query] Object of query parameters
 * @param {object} [session] Current session information
 * @param {number} [retryIndex] Counter for request retry
 * @returns {Promise}
 */
const makeRequest = async function(path, options = {}, session = {}, retryIndex = 0) {
	const requestStartTime = logRequest ? new Date() : null;
	const requestAttempt = retryIndex + 1;
	let fetchUrl = path;
	const fetchOptions = Object.assign({
		method: 'GET',
		headers: {}
	}, options);
	fetchOptions.headers.Accept = 'application/json';
	if (isObject(fetchOptions.form)) {
		fetchOptions.headers['Content-Type'] = 'application/x-www-form-urlencoded';
		fetchOptions.body = serializeUrl(fetchOptions.form);
	}
	if (isObject(fetchOptions.json) || Array.isArray(fetchOptions.json)) {
		fetchOptions.headers['Content-Type'] = 'application/json';
		fetchOptions.body = json.stringify(fetchOptions.json);
	}
	// Add query parameters to the url
	if (isObject(fetchOptions.query)) {
		const queryString = serializeUrl(fetchOptions.query);
		fetchUrl += `${fetchUrl.includes('?') ? '&' : '?'}${queryString}`;
	}
	let response;
	try {
		response = await fetch(fetchUrl, fetchOptions);
	} catch (fetchError) {
		throw appError(fetchError, {
			code: 'APP_SHARED_UTIL',
			origin: 'makeRequest.fetch',
			status: 500,
			detail: {
				fetchUrl,
				fetchOptions,
				fetchError
			}
		});
	}
	// We are using response.text() method as not every response is valid JSON
	const responseText = await response.text();
	const responseJson = responseText.length ? json.parse(responseText) : null;
	const headersEntries = Array.from(response.headers.entries());
	// On client, check if response headers are missing
	checkMissingHeaders(headersEntries, session);
	const responseData = {
		status: response.status,
		headers: Object.fromEntries(headersEntries),
		body: responseJson || responseText
	};
	const responseBodyIsJson = !responseText.length || responseText.length && responseJson;
	logRequest(fetchUrl, fetchOptions, responseData, requestStartTime, session);
	// Norton services returns 200 status code, but it's response body contains "service status" which could mean an error
	const nlokServiceStatus = /\.norton\.com/.test(path) ? get(responseData, 'body.primaryStatus', '0000') : '';
	const nlokServiceReturnedError = nlokServiceStatus.length && nlokServiceStatus !== '0000';
	// If request failed, build detailed object about the request for better debugging
	let requestError;
	if (!response.ok || nlokServiceReturnedError || !responseBodyIsJson) {
		let errorMessage = 'Unexpected error while calling an external URL.';
		if (!responseBodyIsJson) {
			errorMessage += ' Expected response to have JSON format.';
		}
		const errorDetail = {
			attempt: {
				number: requestAttempt
			},
			session,
			request: {
				path
			},
			response: {
				headers: responseData.headers,
				body: responseData.body
			}
		};
		requestError = appError(errorMessage, {
			code: 'APP_SHARED_UTIL',
			origin: 'makeRequest',
			status: responseData.status,
			detail: errorDetail
		});
		// Throw error and don't retry request if it's 500 error cancelled order
		const orderStatus = get(responseData, 'body.orderStatus', '');
		const isCancelledOrder = !['IN-PROGRESS', 'IN_PROGRESS', 'CLOSED'].includes(orderStatus);
		if (requestError && /500/.test(String(responseData.status)) && isCancelledOrder) {
			throw requestError;
		}
	}
	// On client, try to retry the request if it's not last retry and the status code is applicable to retry
	const retryTimeout = requestRetryTimeout[retryIndex];
	const retriableStatusCode = requestRetryPattern.test(String(responseData.status));
	if (IS_CLIENT && retryTimeout && retriableStatusCode) {
		await sleep(retryTimeout);
		return makeRequest(path, options, session, requestAttempt);
	}
	// If request was repeated by retry mechanism, make a log
	if (IS_CLIENT && requestAttempt > 1) {
		appError(`Request succeeded after ${requestAttempt} attempts`, {
			code: 'APP_SHARED_UTIL',
			origin: 'makeRequest'
		});
	}
	// If request failed, throw the appError object
	if (requestError) {
		throw requestError;
	}
	// Return successful request response data
	return responseData;
};

/**
 * Make GET request
 * @param {string} path
 * @param {object} [options]
 * @param {object} [session]
 * @returns {Promise}
 */
makeRequest.get = (path, options = {}, session = {}) => makeRequest(path, Object.assign({}, options, {
	method: 'GET'
}), session);

/**
 * Make POST request
 * @param {string} path
 * @param {object} [options]
 * @param {object} [session]
 * @returns {Promise}
 */
makeRequest.post = (path, options = {}, session = {}) => makeRequest(path, Object.assign({}, options, {
	method: 'POST'
}), session);

/**
 * Make PUT request
 * @param {string} path
 * @param {object} [options]
 * @param {object} [session]
 * @returns {Promise}
 */
makeRequest.put = (path, options = {}, session = {}) => makeRequest(path, Object.assign({}, options, {
	method: 'PUT'
}), session);

/**
 * Make PATCH request
 * @param {string} path
 * @param {object} [options]
 * @param {object} [session]
 * @returns {Promise}
 */
makeRequest.patch = (path, options = {}, session = {}) => makeRequest(path, Object.assign({}, options, {
	method: 'PATCH'
}), session);

/**
 * Make DELETE request
 * @param {string} path
 * @param {object} [options]
 * @param {object} [session]
 * @returns {Promise}
 */
makeRequest.delete = (path, options = {}, session = {}) => makeRequest(path, Object.assign({}, options, {
	method: 'DELETE'
}), session);

export default makeRequest;
